import type { ReactElement } from 'react'
import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'

import { useAuth } from '~/common/hooks/useAuth'

/**
 * Protected route that gates all app pages behind authentication.
 *
 * Unauthenticated users are redirected to /auth/login with a `redirect` param pointing
 * back to where they were headed — Login already navigates there after a successful sign-in.
 */
export const RequireAuth = (): ReactElement | null => {
  const { isSignedIn, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Redirect unauthenticated users to login once the session check has settled
  useEffect(() => {
    if (!isLoading && !isSignedIn) {
      const returnTo = location.pathname + location.search
      void navigate(`/auth/login?redirect=${encodeURIComponent(returnTo)}`, { replace: true })
    }
  }, [isLoading, isSignedIn, location.pathname, location.search, navigate])

  if (isLoading || !isSignedIn) {
    // Session check in flight, or redirect pending — render nothing, matching HydrateFallback
    return null
  }

  return <Outlet />
}
